import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Mail, CheckCircle2, XCircle, FlaskConical, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SmtpAdmin({ auth }) {
    const api = useMemo(() => axios.create({ baseURL: API, headers: auth?.headers || {} }), [auth]);
    const [info, setInfo] = useState(null);
    const [log, setLog] = useState(null);
    const [preview, setPreview] = useState(null);
    const [testing, setTesting] = useState(false);

    const load = useCallback(() => {
        api.get('/tech/smtp').then(r => setInfo(r.data)).catch(() => toast.error('Chargement impossible'));
        api.get('/tech/smtp/log').then(r => setLog(r.data.items)).catch(() => setLog([]));
    }, [api]);
    useEffect(() => { load(); }, [load]);

    const sendTest = async () => {
        const email = window.prompt('Envoyer un email de test à :');
        if (!email?.trim()) return;
        setTesting(true);
        try {
            await api.post('/tech/smtp/test', { email: email.trim() });
            toast.success('Email de test envoyé');
            setTimeout(load, 1500);
        } catch (e) { toast.error(e?.response?.data?.detail || 'Échec'); }
        finally { setTesting(false); }
    };

    const openPreview = async () => {
        try {
            const r = await api.get('/super-admin/email-preview');
            setPreview(r.data);
        } catch { toast.error('Aperçu indisponible'); }
    };

    if (!info) return <div className="flex justify-center py-16"><Loader2 className="animate-spin" /></div>;

    return (
        <div className="space-y-6" data-testid="smtp-admin">
            {/* Configuration */}
            <div className="rounded-xl border border-border bg-surface p-5" data-testid="smtp-config">
                <div className="flex items-center gap-3 mb-4">
                    <h3 className="font-heading text-lg">Configuration</h3>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium ${info.configured ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`} data-testid="smtp-status-badge">
                        {info.configured ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                        {info.configured ? 'Opérationnel' : 'Non configuré'}
                    </span>
                    <div className="ml-auto flex gap-2">
                        <Button size="sm" variant="outline" onClick={openPreview} data-testid="smtp-preview-btn"><Eye size={13} className="mr-1.5" /> Aperçu du template</Button>
                        <Button size="sm" onClick={sendTest} disabled={testing || !info.configured} data-testid="smtp-test-btn">
                            {testing ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <FlaskConical size={13} className="mr-1.5" />} Envoyer un test
                        </Button>
                    </div>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                    {[['Serveur', info.host], ['Port / Sécurité', info.host ? `${info.port} · ${info.security}` : null],
                      ['Expéditeur', info.from_address], ['Compte', info.user]].map(([l, v]) => (
                        <div key={l} className="rounded-lg border border-border p-3">
                            <div className="text-[11px] uppercase tracking-wider text-foreground/50 font-semibold">{l}</div>
                            <div className="mt-1 font-medium">{v || '—'}</div>
                        </div>
                    ))}
                </div>
                <p className="text-[11px] text-foreground/50 mt-3">Le mot de passe SMTP est stocké côté serveur (variables d'environnement) et n'est jamais exposé.</p>
            </div>

            {/* Templates */}
            <div className="rounded-xl border border-border bg-surface p-5" data-testid="smtp-templates">
                <h3 className="font-heading text-lg mb-3">Templates d'emails</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                    {(info.templates || []).map(t => (
                        <div key={t.key} className="rounded-lg border border-border p-3">
                            <div className="font-medium text-sm flex items-center gap-2"><Mail size={13} className="text-primary" /> {t.label}</div>
                            <div className="text-xs text-foreground/55 mt-1">{t.description}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Journal */}
            <div className="rounded-xl border border-border bg-surface p-5" data-testid="smtp-log">
                <h3 className="font-heading text-lg mb-3">Journal des envois <span className="text-xs text-foreground/50 font-body">(200 derniers)</span></h3>
                {!log ? <Loader2 className="animate-spin" /> : log.length === 0 ? (
                    <div className="text-sm text-foreground/50 py-4" data-testid="smtp-log-empty">Aucun envoi journalisé pour l'instant.</div>
                ) : (
                    <div className="max-h-96 overflow-y-auto divide-y divide-border/60">
                        {log.map((e, i) => (
                            <div key={`${e.at}-${i}`} className="flex items-center gap-3 py-2" data-testid="smtp-log-row">
                                {e.ok ? <CheckCircle2 size={14} className="text-emerald-500 shrink-0" /> : <XCircle size={14} className="text-red-500 shrink-0" />}
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm truncate">{e.subject}</div>
                                    <div className="text-[11px] text-foreground/50">{e.to}{e.error && <span className="text-red-600"> · {e.error}</span>}</div>
                                </div>
                                <div className="text-[11px] text-foreground/50 whitespace-nowrap">{e.at ? new Date(e.at).toLocaleString('fr-FR') : ''}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Dialog open={!!preview} onOpenChange={v => !v && setPreview(null)}>
                <DialogContent className="max-w-3xl max-h-[85vh]" data-testid="smtp-preview-dialog">
                    <DialogHeader><DialogTitle className="font-heading text-2xl">Aperçu du template brandé</DialogTitle></DialogHeader>
                    <iframe title="Aperçu email" srcDoc={preview || ''} className="w-full h-[60vh] rounded-lg border border-border bg-white" />
                </DialogContent>
            </Dialog>
        </div>
    );
}
